import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import TopBar from "../components/TopBar";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Search, Users, Sparkles, CreditCard, UserRound, Clapperboard, Instagram, BookOpen, Newspaper, Trash2, Gift } from "lucide-react";
import PromoReel from "../components/PromoReel";
import IGReel from "../components/IGReel";
import MagazineReel from "../components/MagazineReel";
import MagazinePoster from "../components/MagazinePoster";
import CompGrantsDialog from "../components/CompGrantsDialog";

const PLAN_STYLES = {
  "Trial": "bg-[#EEE7DC] text-[#8C7154]",
  "Subscribed": "bg-[#E4EDE4] text-[#506B52]",
  "Past due": "bg-[#F6E3E3] text-[#9E4747]",
  "Canceled": "bg-[#F2EFEB] text-[#9b938b]",
  "No subscription": "bg-[#F2EFEB] text-[#9b938b]",
  "Active seat": "bg-[#E4EDE4] text-[#506B52]",
  "Inactive": "bg-[#F2EFEB] text-[#9b938b]",
};

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-[#EBE5E0] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[#A68A6B] mb-2">
        <Icon size={16} strokeWidth={1.5} />
        <span className="text-[11px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-serif text-3xl text-[#2B2622]">{value}</p>
    </div>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};

export default function AdminDashboard() {
  const [data, setData] = useState({ users: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [reelOpen, setReelOpen] = useState(false);
  const [igOpen, setIgOpen] = useState(false);
  const [magOpen, setMagOpen] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [compOpen, setCompOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // user obj or "ALL"
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/admin/users").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/users").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const runDelete = async () => {
    if (!confirmTarget) return;
    setBusy(true);
    try {
      if (confirmTarget === "ALL") {
        const r = await api.delete("/admin/users");
        toast.success(`Removed ${r.data.deleted_count} account(s).`);
      } else {
        await api.delete(`/admin/users/${confirmTarget.id}`);
        toast.success(`Deleted ${confirmTarget.email}.`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account.");
    } finally {
      setBusy(false);
      setConfirmTarget(null);
    }
  };

  const filters = ["All", "Designer", "Trial", "Subscribed", "Team member", "Client"];

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.users || []).filter((u) => {
      if (q && !(`${u.name} ${u.email}`.toLowerCase().includes(q))) return false;
      if (filter === "All") return true;
      if (filter === "Trial") return u.plan_status === "Trial";
      if (filter === "Subscribed") return u.plan_status === "Subscribed";
      return u.account_type === filter;
    });
  }, [data.users, search, filter]);

  const s = data.stats || {};

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex flex-col" data-testid="admin-dashboard-page">
      <TopBar />
      <main className="max-w-7xl mx-auto w-full px-6 sm:px-8 py-12 flex-1">
        <div className="border-b border-[#EBE5E0] pb-8 mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#A68A6B] mb-2">Admin</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#2B2622]">Accounts</h1>
            <p className="text-[#5C5651] mt-2 text-sm">Everyone who has signed up for WorkRoom.</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button data-testid="open-poster-btn" onClick={() => setPosterOpen(true)}
              className="inline-flex items-center gap-2 rounded-none border border-[#A68A6B] text-[#2B2622] hover:bg-[#A68A6B] hover:text-white uppercase tracking-wider text-xs py-4 px-5 transition-colors">
              <Newspaper size={16} strokeWidth={1.5} /> Magazine Ad
            </button>
            <button data-testid="open-mag-reel-btn" onClick={() => setMagOpen(true)}
              className="inline-flex items-center gap-2 rounded-none border border-[#A68A6B] text-[#2B2622] hover:bg-[#A68A6B] hover:text-white uppercase tracking-wider text-xs py-4 px-5 transition-colors">
              <BookOpen size={16} strokeWidth={1.5} /> Lookbook
            </button>
            <button data-testid="open-ig-reel-btn" onClick={() => setIgOpen(true)}
              className="inline-flex items-center gap-2 rounded-none border border-[#A68A6B] text-[#2B2622] hover:bg-[#A68A6B] hover:text-white uppercase tracking-wider text-xs py-4 px-5 transition-colors">
              <Instagram size={16} strokeWidth={1.5} /> Instagram Reel
            </button>
            <button data-testid="open-promo-reel-btn" onClick={() => setReelOpen(true)}
              className="inline-flex items-center gap-2 rounded-none bg-[#2B2622] hover:bg-[#A68A6B] text-white uppercase tracking-wider text-xs py-4 px-5 transition-colors">
              <Clapperboard size={16} strokeWidth={1.5} /> Promo Reel
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <Stat icon={Users} label="Total" value={s.total ?? "—"} />
          <Stat icon={UserRound} label="Designers" value={s.designers ?? "—"} />
          <Stat icon={Sparkles} label="On Trial" value={s.trials ?? "—"} />
          <Stat icon={CreditCard} label="Subscribed" value={s.subscribed ?? "—"} />
          <Stat icon={Users} label="Clients" value={s.clients ?? "—"} />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="relative w-full sm:w-80">
            <Search size={15} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A68A6B]" />
            <Input data-testid="admin-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-9 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B] text-sm" />
          </div>
          <div className="flex flex-wrap gap-1">
            {filters.map((f) => (
              <button key={f} data-testid={`admin-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-2 text-[11px] uppercase tracking-wider border transition-colors ${
                  filter === f ? "bg-[#A68A6B] text-white border-[#A68A6B]" : "bg-white text-[#5C5651] border-[#EBE5E0] hover:border-[#A68A6B]"
                }`}>
                {f}
              </button>
            ))}
          </div>
          <button data-testid="open-comp-grants-btn" onClick={() => setCompOpen(true)}
            className="inline-flex items-center gap-2 border border-[#A68A6B] text-[#2B2622] hover:bg-[#A68A6B] hover:text-white uppercase tracking-wider text-[11px] py-2 px-4 transition-colors">
            <Gift size={14} strokeWidth={1.5} /> Free memberships
          </button>
          <button data-testid="admin-delete-all-btn" onClick={() => setConfirmTarget("ALL")}
            className="inline-flex items-center gap-2 border border-[#C08A8A] text-[#9E4747] hover:bg-[#9E4747] hover:text-white uppercase tracking-wider text-[11px] py-2 px-4 transition-colors">
            <Trash2 size={14} strokeWidth={1.5} /> Remove all test accounts
          </button>
        </div>

        <div className="border border-[#EBE5E0] bg-white overflow-x-auto">
          {loading ? (
            <p className="p-6 text-sm text-[#5C5651]">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-[#5C5651]" data-testid="admin-no-results">No accounts match your search.</p>
          ) : (
            <table className="w-full text-left" data-testid="admin-users-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-[#A68A6B] border-b border-[#EBE5E0]">
                  <th className="p-4 font-normal">Name</th>
                  <th className="p-4 font-normal">Email</th>
                  <th className="p-4 font-normal">Account</th>
                  <th className="p-4 font-normal">Status</th>
                  <th className="p-4 font-normal">Signed up</th>
                  <th className="p-4 font-normal text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-b border-[#F2EFEB] last:border-0 hover:bg-[#FBFAF8]" data-testid={`admin-user-row-${u.id}`}>
                    <td className="p-4 text-sm text-[#2B2622]">{u.name || "—"}</td>
                    <td className="p-4 text-sm text-[#5C5651]">{u.email}</td>
                    <td className="p-4 text-sm text-[#5C5651]">{u.account_type}</td>
                    <td className="p-4">
                      <span className={`inline-block px-2.5 py-1 text-[11px] uppercase tracking-wider ${PLAN_STYLES[u.plan_status] || "bg-[#F2EFEB] text-[#5C5651]"}`}>
                        {u.plan_status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-[#5C5651]">{fmtDate(u.created_at)}</td>
                    <td className="p-4 text-right">
                      {u.account_type === "Admin" ? (
                        <span className="text-[11px] uppercase tracking-wider text-[#C9BBAE]">—</span>
                      ) : (
                        <button data-testid={`admin-delete-user-${u.id}`} onClick={() => setConfirmTarget(u)}
                          className="inline-flex items-center gap-1.5 text-[#9E4747] hover:text-white hover:bg-[#9E4747] border border-transparent hover:border-[#9E4747] px-2.5 py-1.5 text-[11px] uppercase tracking-wider transition-colors">
                          <Trash2 size={14} strokeWidth={1.5} /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
      <Footer />
      {reelOpen && <PromoReel onClose={() => setReelOpen(false)} />}
      {igOpen && <IGReel onClose={() => setIgOpen(false)} />}
      {magOpen && <MagazineReel onClose={() => setMagOpen(false)} />}
      {posterOpen && <MagazinePoster onClose={() => setPosterOpen(false)} />}
      <CompGrantsDialog open={compOpen} onOpenChange={setCompOpen} />

      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTarget === "ALL" ? "Remove all test accounts?" : "Delete this account?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget === "ALL"
                ? "This permanently deletes every account except your admin account, along with their projects, uploads and all associated data. This cannot be undone."
                : `This permanently deletes ${confirmTarget?.email || "this account"} and all of their projects, uploads and associated data. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel" disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-delete-confirm" disabled={busy}
              onClick={(e) => { e.preventDefault(); runDelete(); }}
              className="bg-[#9E4747] hover:bg-[#843b3b]">
              {busy ? "Deleting…" : (confirmTarget === "ALL" ? "Delete all" : "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
